import * as yup from 'yup'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { BusinessEntityType, RequestedLimitSource } from 'src/constants/oatfi-business/enums'
import { MAX_REQUESTED_CREDIT_LIMIT_CENTS } from 'src/constants/oatfi-business/constants'
import { formatCentsAsDollars } from 'src/utils/oatfiBusiness'

const REQUIRED_MESSAGE = 'Required'
const MONTH_VALUES = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12']

/**
 * Validates the whole profile form before submit.
 *
 * Two of these rules exist because the backend does NOT enforce them, and a bad value would
 * reach Oatfi verbatim:
 *
 * - `contactEmail` is only `@IsOptional() @IsString()` on the wire — there is no `@IsEmail()`.
 * - `requestedCreditLimit` carries only `@IsOptional()` — no `@IsNumber`, `@IsInt` or `@Min`.
 *   It is sent in CENTS, so a decimal or negative is always a mistake.
 *
 * The nine required fields ARE `@IsNotEmpty()` on the wire; they are validated here because
 * Retool marked only `Name`, which turned every other omission into an unexplained 400.
 */
export const businessProfileFormValidator = yup.object({
  name: yup.string().trim().required(REQUIRED_MESSAGE),
  city: yup.string().trim().required(REQUIRED_MESSAGE),
  streetAddress: yup.string().trim().required(REQUIRED_MESSAGE),
  state: yup.string().trim().required(REQUIRED_MESSAGE),
  postalCode: yup.string().trim().required(REQUIRED_MESSAGE),
  taxId: yup.string().trim().required(REQUIRED_MESSAGE),
  contactFirstName: yup.string().trim().required(REQUIRED_MESSAGE),
  contactLastName: yup.string().trim().required(REQUIRED_MESSAGE),
  // Mirrors @IsPhoneNumber('US'). libphonenumber-js is already a dependency of this app.
  //
  // `isValidPhoneNumber(value, 'US')` alone is NOT enough here: the second argument is only a
  // default-country hint used when `value` carries no country code of its own. A fully-qualified
  // non-US number (e.g. '+445555555555') already specifies its own country and still validates
  // as "valid" with that call, silently accepting a non-US number. Parsing and checking the
  // resolved country explicitly is what actually rejects it.
  contactPhoneNumber: yup
    .string()
    .trim()
    .required(REQUIRED_MESSAGE)
    .test('is-us-phone', 'Enter a valid US phone number', (value) => {
      if (!value) {
        return false
      }

      const parsedPhoneNumber = parsePhoneNumberFromString(value, 'US')

      return Boolean(
        parsedPhoneNumber && parsedPhoneNumber.country === 'US' && parsedPhoneNumber.isValid(),
      )
    }),

  dbaName: yup.string().trim(),
  contactEmail: yup.string().trim().email('Enter a valid email address'),
  businessStartDateMonth: yup
    .string()
    .trim()
    .oneOf(['', ...MONTH_VALUES], 'Select a month'),
  businessStartDateYear: yup
    .string()
    .trim()
    .matches(/^$|^\d{4}$/, 'Select a year'),
  businessEntityType: yup
    .string()
    .trim()
    .oneOf(['', ...Object.values(BusinessEntityType)], 'Select an entity type'),
  requestedLimitSource: yup
    .string()
    .trim()
    .oneOf(['', ...Object.values(RequestedLimitSource)], 'Select a limit source'),
  requestedCreditLimit: yup
    .string()
    .trim()
    .matches(/^$|^\d+$/, 'Enter a whole number of cents, with no decimal point')
    .test(
      'within-int4',
      `Too large — the maximum is ${MAX_REQUESTED_CREDIT_LIMIT_CENTS} cents (${
        formatCentsAsDollars(MAX_REQUESTED_CREDIT_LIMIT_CENTS) ?? ''
      }).`,
      (value) => {
        if (value === undefined || value === '' || !/^\d+$/.test(value)) {
          return true
        }

        return Number(value) <= MAX_REQUESTED_CREDIT_LIMIT_CENTS
      },
    ),
})
